import { MessageCircle, Plus, Shield } from "lucide-react";

import type { GtmBrainDoc } from "../api/GtmApi";
import { listObjections } from "./objections";

type Props = {
  docs: GtmBrainDoc[];
  onOpenObjection: (key: string) => void;
  onAddObjection: () => void;
  onBackToOverview: () => void;
};

export default function BrainObjectionsPanel({
  docs,
  onOpenObjection,
  onAddObjection,
  onBackToOverview,
}: Props) {
  const objections = listObjections(docs);

  return (
    <div className="flex h-full w-[22rem] shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
        <button
          type="button"
          className="hover:text-slate-800 hover:underline"
          onClick={onBackToOverview}
        >
          Brain
        </button>
        <span className="mx-1.5 text-slate-300">›</span>
        <span className="font-medium text-slate-800">Objections and responses</span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50">
                <MessageCircle className="h-4 w-4 text-sky-600" />
              </span>
              <h2 className="text-base font-semibold text-slate-900">Objections and responses</h2>
            </div>
            <p className="text-sm text-slate-500">
              Objections you’ve heard and how you answer them.
            </p>
          </div>
          <button
            type="button"
            onClick={onAddObjection}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add objection
          </button>
        </div>

        {objections.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-slate-50/60 px-6 py-14 text-center">
            <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-100">
              <Shield className="h-5 w-5 text-rose-400" />
            </span>
            <p className="text-sm font-medium text-slate-800">
              Nothing in Objections and responses yet.
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Use &apos;Add objection&apos; above to start the first one.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {objections.map((o) => (
              <li key={o.key}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-3.5 text-left transition-colors hover:bg-slate-50"
                  onClick={() => onOpenObjection(o.key)}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50">
                    <Shield className="h-4 w-4 text-rose-500" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block line-clamp-2 text-sm font-medium text-slate-900">
                      {o.label}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {o.filled} of {o.total} sections filled
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
